import { useShallow } from 'zustand/react/shallow'

import { mainSurgeon } from '~/app/pages/shared/waitingListFilters'
import { isPatientAvailableOnAnyDayInDateRange } from '~/app/pages/WaitingList/Drawer/PatientDrawerTab/StatusesAndRemarks/PatientAvailability/utils'
import { getToday, isFutureMonth, isSameOrAfter, subtract } from '~/astra'
import { mergeQueryStatuses, useScheduledSurgeriesQueries } from '~/hooks/tanstack-query'
import { useSurgeonWaitingListPredictionQuery, WaitingListPredictionQueryOptions } from '~/hooks/tanstack-query/optimus/useSurgeonWaitingListPredictionQuery'
import { useBaseCurrentWaitingListForStaffing } from '~/hooks/useBaseCurrentWaitingListForStaffing'
import { getFlag } from '~/hooks/useFlag'
import { selectPractitionerValues } from '~/store/slices/surgeonSchedulerFilterSlice'
import { useStore } from '~/store/store'
import { Practitioner, useActivePractitioners, useFutureOptimisticScheduledSurgeries } from '~/storm'

import { WaitingListCalloutType } from '../../shared'
import { useResolvedDepartmentByActiveSpecialities } from '../../shared/resolvedDepartment'
import { calculateSurgeonWaitingListPrediction, type PredictedWaitingListResult } from './calculateSurgeonWaitingListPrediction'

type PredictedWaitingListEnabledResult =
    | {
          enabled: true
          reason: undefined
      }
    | {
          enabled: false
          reason: Extract<WaitingListCalloutType, 'noPredictionsAllDepartments' | 'noPredictionsSameOrPastMonth' | 'noPredictionsAllSpecialities'>
      }

export const useIsPredictedWaitingListEnabled = (): PredictedWaitingListEnabledResult => {
    const isSpecialtiesSelectionEnabled = getFlag('use_specialties_selection_filter')
    const departmentResolution = useResolvedDepartmentByActiveSpecialities()
    const { selectedDate } = useStore(useShallow(state => ({ selectedDate: state.appFilters.selectedDate })))
    // do not allow predicted waiting list for all departments
    // its very likely that users do not have access to data/(scheduledSurgeries) across departments
    if (departmentResolution.status === 'all') {
        return { enabled: false, reason: isSpecialtiesSelectionEnabled ? 'noPredictionsAllSpecialities' : 'noPredictionsAllDepartments' }
    }
    // predicted waiting lists are only relevant for future months
    if (!isFutureMonth(selectedDate)) {
        return { enabled: false, reason: 'noPredictionsSameOrPastMonth' }
    }
    return { enabled: true, reason: undefined }
}

const useActivePractitionersWaitingListPrediction = (queryOptions?: WaitingListPredictionQueryOptions) => {
    const selectedDate = useStore(state => state.appFilters.selectedDate)
    const filteredPractitioners = useStore(selectPractitionerValues)

    const today = getToday()
    const lastDayOfPreviousMonth = subtract(selectedDate, 1, 'month').endOf('month')
    const firstDayOfMonth = selectedDate.startOf('month')
    const lastDayOfMonth = selectedDate.endOf('month')

    const { enabled: predictedWaitingListEnabled } = useIsPredictedWaitingListEnabled()

    const scheduledQueries = useScheduledSurgeriesQueries({
        startDate: today,
        endDate: lastDayOfMonth,
        options: { staleTime: Infinity, skip: !predictedWaitingListEnabled },
    })

    const activePractitioners = useActivePractitioners()
    const practitionerShortNames = activePractitioners
        .filter(p => filteredPractitioners.length === 0 || filteredPractitioners.includes(p.short_name))
        .map(p => p.short_name)

    const currentWaitingListQuery = useBaseCurrentWaitingListForStaffing({ skip: !predictedWaitingListEnabled })

    const predictionQueryOptions = {
        ...queryOptions,
        // dependent on surgeries from the store after they are fetched
        enabled: scheduledQueries.isSuccess,
    }
    const predictionUntilStartOfMonth = useSurgeonWaitingListPredictionQuery(
        {
            startDate: today,
            endDate: lastDayOfPreviousMonth,
            surgeonNames: practitionerShortNames,
        },
        predictionQueryOptions
    )

    const predictionThisMonth = useSurgeonWaitingListPredictionQuery(
        {
            startDate: firstDayOfMonth,
            endDate: lastDayOfMonth,
            surgeonNames: practitionerShortNames,
        },
        predictionQueryOptions
    )

    return {
        predictionUntilStartOfMonth,
        predictionThisMonth,
        selectedDate,
        firstDayOfMonth,
        lastDayOfMonth,
        lastDayOfPreviousMonth,
        statuses: mergeQueryStatuses(predictionUntilStartOfMonth, predictionThisMonth, scheduledQueries, currentWaitingListQuery),
        surgeries: currentWaitingListQuery.surgeriesWithMetadata,
    }
}

const emptyPredictedWaitingListResult: PredictedWaitingListResult = {
    isError: false,
    isLoading: false,
    currentUnscheduledWaitingList: 0,
    futureBookedSurgeries: 0,
    availableCapacityUntilThisMonth: 0,
    increaseUntilThisMonth: 0,
    waitingListStartOfMonth: 0,
    availableCapacityThisMonth: 0,
    increaseThisMonth: 0,
    waitingListEndOfMonth: 0,
    alreadyBookedSurgeriesThisMonth: 0,
    totalPredictedSurgeriesThisMonth: 0,
    isPredictedBelowSafeLevels: false,
}

export const usePractitionerPredictedWaitingList = (
    practitioner: Practitioner,
    queryOptions?: WaitingListPredictionQueryOptions
): PredictedWaitingListResult => {
    const { predictionUntilStartOfMonth, predictionThisMonth, firstDayOfMonth, lastDayOfMonth, statuses, surgeries } =
        useActivePractitionersWaitingListPrediction(queryOptions)

    const showOnlyAvailable = useStore(state => state.surgeonScheduler.showOnlyAvailableWaitingListPatients)
    const futureOptimisticScheduledSurgeries = useFutureOptimisticScheduledSurgeries()

    const waitingListForSurgeon = surgeries.filter(mainSurgeon([practitioner.short_name]))

    if (statuses.isLoading) {
        return {
            ...emptyPredictedWaitingListResult,
            isLoading: true,
        }
    }

    const predictionUntilStartOfMonthResult = predictionUntilStartOfMonth.data?.waitinglistBySurgeonName[practitioner.short_name]
    const predictionThisMonthResult = predictionThisMonth.data?.waitinglistBySurgeonName[practitioner.short_name]
    if (statuses.isError || !predictionUntilStartOfMonthResult || !predictionThisMonthResult) {
        return {
            ...emptyPredictedWaitingListResult,
            isError: predictionUntilStartOfMonth.isError || predictionThisMonth.isError,
        }
    }
    const currentUnscheduledWaitingList = showOnlyAvailable
        ? waitingListForSurgeon.filter(surgery => isPatientAvailableOnAnyDayInDateRange(firstDayOfMonth, lastDayOfMonth, surgery)).length
        : waitingListForSurgeon.length

    const futureBookedSurgeries = futureOptimisticScheduledSurgeries
        .filter(mainSurgeon([practitioner.short_name]))
        .filter(surgery => isSameOrAfter(surgery.contact?.checkInTime, firstDayOfMonth)).length

    const predictedWaitingListResult = calculateSurgeonWaitingListPrediction({
        predictionUntilStartOfMonth: predictionUntilStartOfMonthResult,
        predictionThisMonth: predictionThisMonthResult,
        currentUnscheduledWaitingList,
        futureBookedSurgeries,
    })

    return predictedWaitingListResult
}
